import React, { RefObject, useState } from 'react';
import { SketchPicker, SketchPickerProps } from 'react-color';
import { SxProps, Theme } from '@mui/material';
import { TextField, TextFieldProps } from '../Textfield';

import { PopperProps, Popper } from '../Popper';
import { Icon } from '../Icon';

type PopperPosition = 'bottom-end' | 'bottom-start';

export type ColorPickerProps = Omit<SketchPickerProps, 'onChange'> & {
  pickerProps?: SketchPickerProps;
  pickerDataProp?: string;
  popperPosition?: PopperPosition;
  labelText?: string;
  value: string;
  onChange: (color: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  sxPopper?: SxProps<Theme>;
  sxTextField?: SxProps<Theme>;
};

const styles: Record<string, SxProps<Theme>> = {
  paper: {
    padding: 0,
    fontFamily: 'Lato,Roboto,"Helvetica Neue",Arial,sans-serif'
  },
  popper: {
    zIndex: 'modal'
  },
  pickerInputLabel: {
    '& label': {
      '& focus': {
        color: 'primary.main',
        fontWeight: 700
      }
    },
    '& [class*="MuiInputBase-input"]': {
      outline: 0
    }
  }
};

const ColorPicker: React.FunctionComponent<ColorPickerProps> = ({
  popperPosition,
  labelText,
  value,
  pickerDataProp,
  onChange,
  required,
  error,
  helperText,
  sxPopper,
  sxTextField,
  ...passedPickerProps
}) => {
  const { paper, popper, pickerInputLabel } = styles;
  const [open, setOpen] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const pickerProps: SketchPickerProps = {
    presetColors: [],
    disableAlpha: true,
    width: '200px',
    color: value,
    onChangeComplete: data => {
      onChange(data.hex);
    },
    ...passedPickerProps
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const keyListeners = {
    escape: 'Escape',
    enter: 'Enter'
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { enter, escape } = keyListeners;
    const { key } = event;
    if (key === enter) {
      setOpen(previousOpen => !previousOpen);
    }

    if (key === escape) {
      setOpen(false);
    }
  };

  const menu = <SketchPicker {...pickerProps} />;

  const textFieldProps: TextFieldProps = {
    InputProps: { readOnly: true, endAdornment: <Icon style={{ color: value }} icon={'Lens'} /> },
    onClick: () => {
      setOpen(previousOpen => !previousOpen);
    },
    value: value,
    ref: inputRef,
    label: labelText,
    required,
    error,
    helperText,
    onKeyDown: (event: React.KeyboardEvent) => handleKeyDown(event)
  };
  const { ref, ...restTextFieldProps } = textFieldProps;
  const trigger = (
    <TextField
      sx={[pickerInputLabel, sxTextField] as SxProps<Theme>}
      ref={ref as RefObject<HTMLInputElement>}
      {...restTextFieldProps}
      data-selector={pickerDataProp}
    />
  );

  const popperProps: PopperProps = {
    handleClickAway,
    menu,
    sxPaper: paper,
    open,
    popperAnchorRef: inputRef,
    sx: [popper, sxPopper] as SxProps<Theme>,
    popperPosition,
    trigger
  };
  return <Popper {...popperProps} />;
};

ColorPicker.defaultProps = {
  popperPosition: 'bottom-end'
};

export { ColorPicker };
